/**
 * Authentication modal component - Login only
 * Registration is handled via invite links only
 */

import { useState } from 'react';
import { LoginForm } from './LoginForm';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigateToForgotPassword?: () => void;
}

export function AuthModal({ isOpen, onClose, onNavigateToForgotPassword }: AuthModalProps) {
  // Track when 2FA step is active to prevent accidental modal close
  const [is2FAActive, setIs2FAActive] = useState(false);

  if (!isOpen) return null;

  // Only allow overlay click to close when not in 2FA step
  const handleOverlayClick = () => {
    if (!is2FAActive) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay - disabled during 2FA to prevent accidental close */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleOverlayClick}
          onTouchEnd={handleOverlayClick}
        ></div>

        {/* Modal content */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full max-h-[90vh] overflow-y-auto">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            {/* Close button */}
            <div className="absolute top-0 right-0 pt-4 pr-4">
              <button
                type="button"
                className="bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 p-2 min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
                onClick={onClose}
                onTouchEnd={onClose}
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Login form only */}
            <LoginForm onClose={onClose} onForgotPassword={onNavigateToForgotPassword} onTwoFactorStateChange={setIs2FAActive} />
          </div>
        </div>
      </div>
    </div>
  );
}
